/**
 * Shrinks a photograph before it is queued for upload.
 *
 * A phone's own camera, reached through the file picker, hands over the full
 * sensor image: 9 to 10 MB from a current handset. The extraction model on
 * Bedrock refuses any image over 5 MB, so every capture taken that way failed
 * before a reviewer could see it. A longest edge of 2048 px keeps nameplate
 * text legible and lands well under 1 MB at JPEG quality 0.85.
 *
 * Every frame passes through here: the shutter, the picker, a dropped file
 * and a retake or added frame on the result screen. A JPEG already inside the
 * edge limit is returned untouched, so a viewfinder frame is not re-encoded
 * twice. Orientation is applied at decode, so the stored pixels are upright
 * without relying on EXIF, which re-encoding drops.
 */

export const MAX_UPLOAD_EDGE_PX = 2048;
export const UPLOAD_JPEG_QUALITY = 0.85;

export interface ImageSize {
  width: number;
  height: number;
}

/** The size that fits `maxEdge` on the longest side, never upscaled. */
export function fitWithin(size: ImageSize, maxEdge = MAX_UPLOAD_EDGE_PX): ImageSize {
  const longest = Math.max(size.width, size.height);
  if (longest <= maxEdge) return { width: size.width, height: size.height };
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(size.width * scale)),
    height: Math.max(1, Math.round(size.height * scale)),
  };
}

export interface DownscaleDeps {
  /** Decodes with orientation applied. Throws when the browser cannot decode the format. */
  decode: (blob: Blob) => Promise<ImageBitmap>;
  /** Draws the bitmap at `size` and encodes it as JPEG. */
  encode: (bitmap: ImageBitmap, size: ImageSize, quality: number) => Promise<Blob>;
}

function canvasFor(size: ImageSize): HTMLCanvasElement | OffscreenCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(size.width, size.height);
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  return canvas;
}

export const browserDownscaleDeps: DownscaleDeps = {
  decode: (blob) => createImageBitmap(blob, { imageOrientation: 'from-image' }),
  async encode(bitmap, size, quality) {
    const canvas = canvasFor(size);
    const ctx = canvas.getContext('2d') as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D
      | null;
    if (!ctx) throw new Error('2D canvas context unavailable for resizing the photograph');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, size.width, size.height);
    if ('convertToBlob' in canvas) {
      return canvas.convertToBlob({ type: 'image/jpeg', quality });
    }
    return new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the resized photograph'))),
        'image/jpeg',
        quality,
      ),
    );
  },
};

/**
 * Returns a JPEG whose longest edge is at most 2048 px. A decode failure is
 * rethrown, so the caller's unsupported-format message still applies.
 */
export async function downscaleForUpload(
  blob: Blob,
  deps: DownscaleDeps = browserDownscaleDeps,
): Promise<Blob> {
  const bitmap = await deps.decode(blob);
  try {
    const source = { width: bitmap.width, height: bitmap.height };
    const target = fitWithin(source);
    const unchanged = target.width === source.width && target.height === source.height;
    if (unchanged && blob.type === 'image/jpeg') return blob;
    return await deps.encode(bitmap, target, UPLOAD_JPEG_QUALITY);
  } finally {
    bitmap.close?.();
  }
}
